import { sortBy } from 'es-toolkit';
import { compareDates } from '../date/date-compare';
import { compareStrings } from '../string/string-compare';
import { parseISO } from 'date-fns';
import { CountryNominationSummary } from '../../models';
import { NominationSummarySortKey } from './nomination-summary-sort-key';
import { getRoleRank } from './get-role-rank';
import { getUserNames } from '../string/get-user-names';

export const sortCountryNominationSummariesByKey = (nominationSummaries: CountryNominationSummary[], key: NominationSummarySortKey, isDescending: boolean): CountryNominationSummary[] => {
  if (key === 'role') {
    const sorted = sortBy(nominationSummaries, [(summary) => getRoleRank(summary.role)]);
    return isDescending ? sorted.reverse() : sorted;
  }

  if (key === 'score') {
    return sortBy(nominationSummaries, [
      (summary) => getRoleRank(summary.role),
      (summary) => isDescending ? -(summary.score ?? 0) : (summary.score ?? 0),
    ]);
  }

  if (key === 'scrubbing-status') {
    const sorted = sortBy(nominationSummaries, [nomination => nomination.scrubbingStatusDisplayName]);
    return isDescending ? sorted.reverse() : sorted;
  }

  if (key === 'scrubbing-by') {
    const sorted = sortBy(nominationSummaries, [nomination => nomination.scrubbedBy]);
    return isDescending ? sorted.reverse() : sorted;
  }

  if (key === 'scrubbing-by-assigned-date') {
    const sorted = sortBy(nominationSummaries, [nomination => nomination.scrubbedByAssignedDate]);
    return isDescending ? sorted.reverse() : sorted;
  }

  if (key === 'assigned-to') {
    const sorted = sortBy(nominationSummaries, [nomination => getUserNames(nomination.judges.map(judge => judge.user))]);
    return isDescending ? sorted.reverse() : sorted;
  }

  if (key === 'assigned-date') {
    const sorted = sortBy(nominationSummaries, [(nomination) => {
      const assignedJudge = nomination.judges.at(0);
      return assignedJudge ? parseISO(assignedJudge.assignmentDate) : undefined;
    }]);
    return isDescending ? sorted.reverse() : sorted;
  }

  if (key === 'judged') {
    const sorted = sortBy(nominationSummaries, [nomination => nomination.numberOfCompletedJudgment]);
    return isDescending ? sorted.reverse() : sorted;
  }

  return [...nominationSummaries]
    .sort((countryNominationSummaryA, countryNominationSummaryB) => {
      let result = 0;
      switch (key) {
        case 'title':
          result = compareStrings(countryNominationSummaryA.title, countryNominationSummaryB.title);
          break;
        case 'status':
          result = compareStrings(
            countryNominationSummaryA.scrubbingStatusId > 0 ? countryNominationSummaryA.scrubbingStatusDisplayName : countryNominationSummaryA.statusDisplayName,
            countryNominationSummaryB.scrubbingStatusId > 0 ? countryNominationSummaryB.scrubbingStatusDisplayName : countryNominationSummaryB.statusDisplayName
          );
          break;
        case 'alias':
          result = compareStrings(countryNominationSummaryA.alias, countryNominationSummaryB.alias);
          break;
        case 'last-edited':
          result = compareDates(
            countryNominationSummaryA.lastEditedDate ? parseISO(countryNominationSummaryA.lastEditedDate) : undefined,
            countryNominationSummaryB.lastEditedDate ? parseISO(countryNominationSummaryB.lastEditedDate) : undefined);
          break;
        case 'company':
          result = compareStrings(countryNominationSummaryA.company, countryNominationSummaryB.company);
          break;
        case 'questionnaire-last-updated-date':
          result = compareDates(
            countryNominationSummaryA.questionnaireSummaries && countryNominationSummaryA.questionnaireSummaries.length > 0 && countryNominationSummaryA.questionnaireSummaries[0].lastUpdatedDate
              ? parseISO(countryNominationSummaryA.questionnaireSummaries[0].lastUpdatedDate)
              : undefined,
            countryNominationSummaryB.questionnaireSummaries && countryNominationSummaryB.questionnaireSummaries.length > 0 && countryNominationSummaryB.questionnaireSummaries[0].lastUpdatedDate
              ? parseISO(countryNominationSummaryB.questionnaireSummaries[0].lastUpdatedDate)
              : undefined);
          break;
        case 'questionnaire-status':
          result = compareStrings(
            countryNominationSummaryA.questionnaireSummaries && countryNominationSummaryA.questionnaireSummaries.length > 0 && countryNominationSummaryA.questionnaireSummaries[0].status ?
              countryNominationSummaryA.questionnaireSummaries[0].status : undefined,
            countryNominationSummaryB.questionnaireSummaries && countryNominationSummaryB.questionnaireSummaries.length > 0 && countryNominationSummaryB.questionnaireSummaries[0].status ?
              countryNominationSummaryB.questionnaireSummaries[0].status : undefined
          );
          break;
        default:
          return 0;
      }
      return isDescending ? result * -1 : result;
    });
}
